<template>
  <v-div
    ref="videoContainer"
    class="AssetDisplay"
    relative
    pa-2
    d-inline-block
    :height="displayHeight"
    width-100-percent
    overflow-visible
  >
    <v-div height-100-percent width-100-percent overflow-hidden>
      <div v-show="assetReady" class="full-height">
        <v-div
          ref="videoPlayer"
          absolute
          layer-1
          height-100-percent
          width-100-percent
          overflow-hidden
        />
        <v-div v-if="showVimeoError" absolute pa-2>
          <v-alert :value="true" type="warning" :icon="false" width="100%">
            <h4>This video is not ready to play</h4>
            <span>Please try again soon</span>
            <v-div caption partially-transparent text-right mt-2t
              >Reference {{ videoReference.id }}</v-div
            >
          </v-alert>
        </v-div>
        <img
          v-if="imageSource"
          class="thumbnail"
          alt=""
          :src="imageSource"
          :hidden="!hideVideoPlayer"
        />

        <button
          v-if="asset && canDelete"
          class="v-div trigger absolute layer-2 pt-2 rounded-32 height-48 width-48 left-0 inverted--text secondary text-center has-action overflow-hidden"
          :style="
            canDownload && canPlay ? { bottom: '116px' } : { bottom: '58px' }
          "
          @click="onDelete"
        >
          <span class="d-sr-only">delete file {{ asset.fileName }}</span>
          <v-icon aria-hidden="true" dark>{{ mdiDelete }}</v-icon>
          <span class="hidden" aria-hidden="true">Delete File</span>
        </button>

        <button
          v-if="asset && canPlay"
          class="v-div trigger absolute layer-2 pt-2 rounded-32 height-48 width-48 left-0 inverted--text secondary text-center has-action overflow-hidden"
          :style="{ bottom: canDownload ? '58px' : '0px' }"
          @click="onPlayVideo"
        >
          <span class="d-sr-only">play video {{ asset.fileName }}</span>
          <v-icon aria-hidden="true" dark>{{ mdiPlay }}</v-icon>
          <span class="hidden">Play Video</span>
        </button>

        <button
          v-if="asset && canDownload"
          class="v-div trigger absolute layer-3 pt-2 rounded-32 height-48 width-48 left-0 bottom-0 inverted--text secondary text-center has-action overflow-hidden"
          :data-test-id="dataTestId"
          @click="onDownload"
        >
          <span class="d-sr-only"
            >download file {{ asset.fileName }} {{ fileSize }} MB [opens in a
            new tab]</span
          >
          <v-icon aria-hidden="true" dark>{{ mdiCloudDownload }}</v-icon>
          <span class="hidden" aria-hidden="true"
            >{{ fileType }} : {{ fileSize }} MB</span
          >
        </button>
      </div>
      <div v-show="assetNotReady" class="full-height">
        <v-div background-warning foreground-inverted elevation-4 pa-4 body-2>
          <span
            >We are encoding your file so that it is compatible across browsers,
            depending on file size and type this may take some time.</span
          >
          <div
            class="v-progress-circular v-progress-circular--indeterminate"
            style="height: 16px; width: 16px"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              viewBox="21.333333333333332 21.333333333333332 42.666666666666664 42.666666666666664"
              style="transform: rotate(0deg)"
            >
              <circle
                fill="transparent"
                cx="42.666666666666664"
                cy="42.666666666666664"
                r="20"
                stroke-width="2.6666666666666665"
                stroke-dasharray="125.664"
                stroke-dashoffset="125.66370614359172px"
                class="v-progress-circular__overlay"
              />
            </svg>
          </div>
          <v-div v-if="showRetrigger && videoReference.videoId">
            <v-div d-inline caption mt-4 mb-2
              >Processing Started
              {{ videoReference.updatedAt | timeInterval }} ago</v-div
            >
            <v-btn small color="error" @click="onRetriggerProcessing"
              >Retrigger Processing</v-btn
            >
            <v-div caption partially-transparent text-right mt-2
              >Reference {{ videoReference.videoId }}</v-div
            >
          </v-div>
        </v-div>
      </div>
    </v-div>
  </v-div>
</template>

<script>
import { mdiDelete, mdiPlay, mdiCloudDownload } from "@mdi/js";
import { mapState } from "vuex";
import mapFlags from "~/plugins/utilities/mapFlags";

import loadVimeoVideoPlayer from "~/components/articles/components/utilities/loadVimeoVideoPlayer";

const DOC_TYPES = [
  ".doc",
  ".dot",
  ".docx",
  ".docm",
  ".dotx",
  ".dotm",
  ".wpd",
  ".wps",
  ".sdw",
  ".sgl",
  ".vor",
  ".uot",
  ".uof",
  ".jtd",
  ".jtt",
  ".hwp",
  ".pdb",
  ".psw",
  ".rtf",
  ".htm",
  ".html",
];

const XLS_TYPES = [
  ".xls",
  ".xlw",
  ".xlt",
  ".xls",
  ".xlw",
  ".xlt",
  ".xlsx",
  ".xlsm",
  ".xltx",
  ".xltm",
  ".xlsb",
  ".wk1",
  ".wks",
  ".123",
  ".sdc",
  ".vor",
  ".slk",
  ".uos",
  ".uof",
  ".pxl",
  ".wb2",
];

const CSV_TYPES = [".csv", "dbf", "dif"];

export default {
  name: "AssetDisplay",
  props: {
    item: { type: Object, default: undefined },
    asset: { type: Object, default: undefined },
    supplementaryTitle: { type: String, default: "" },
    small: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    videoUsage: { type: Boolean, default: false },
    videoEvent: { type: String, default: "DL-OR" },
    showRetrigger: { type: Boolean, default: false },
    dataTestId: {
      type: [String, null],
      default: null,
    },
  },
  data() {
    return {
      hideVideoPlayer: true,
      hideVideoIcon: false,
      showVimeoError: false,
      playingVideo: false,
      // Icons
      mdiDelete,
      mdiPlay,
      mdiCloudDownload,
    };
  },
  computed: {
    ...mapState({
      theme: (state) => state.theme,
      isStartingVideo: (state) => state.selectedItem.isStartingVideo,
    }),
    ...mapFlags(["allowVideoDownload"]),
    fileName() {
      if (this.asset && this.asset.fileName) {
        return this.asset.fileName.split(".")[0];
      }

      return undefined;
    },
    fileType() {
      if (this.asset && this.asset.fileName) {
        const bits = this.asset.fileName.split(".");
        const lastBit = bits.length - 1;

        if (lastBit >= 0) {
          return bits[lastBit];
        }
      }

      return undefined;
    },
    fileSize() {
      if (this.asset && this.asset.fileSizeMegabytes) {
        if (this.asset.fileSizeMegabytes > 1) {
          return Math.floor(this.asset.fileSizeMegabytes).toString();
        }

        return this.asset.fileSizeMegabytes.toFixed(2);
      }

      return undefined;
    },
    blankFile() {
      return this.theme.images.blankFile;
    },
    csvFile() {
      return this.theme.images.csvFile;
    },
    docFile() {
      return this.theme.images.docFile;
    },
    xlsFile() {
      return this.theme.images.xlsFile;
    },
    imageSource() {
      if (!this.asset) {
        return;
      }

      let url;

      if (this.asset.largeThumbnail && this.asset.largeThumbnail.url) {
        url = this.asset.largeThumbnail.url;
      } else if (this.asset.mediumThumbnail && this.asset.mediumThumbnail.url) {
        url = this.asset.mediumThumbnail.url;
      } else if (this.asset.smallThumbnail && this.asset.smallThumbnail.url) {
        url = this.asset.smallThumbnail.url;
      }

      if (!url) {
        const asset = this.asset || {};
        const original = asset.original || {};
        const fileName = original.url || "";
        const fileType = (fileName.match(/\.[0-9a-z]+$/i) || [])[0];

        if (DOC_TYPES.includes(fileType)) {
          url = this.docFile;
        } else if (XLS_TYPES.includes(fileType)) {
          url = this.xlsFile;
        } else if (CSV_TYPES.includes(fileType)) {
          url = this.csvFile;
        } else {
          url = this.blankFile;
        }
      }

      return url;
    },
    fileSource() {
      if (!this.asset) {
        return;
      }

      return this.asset.original?.url;
    },
    canDownload() {
      if (this.isVideo) return this.allowVideoDownload && this.canPlay;

      return true;
    },
    canPlay() {
      return (
        this.isVideo && !this.hideVideoIcon && this.asset.status === "COMPLETED"
      );
    },
    isVideo() {
      return this.asset.embeddableVideo
        ? !!this.asset.embeddableVideo.videoId
        : false;
    },
    hideVideoButton() {
      return !this.isVideo || this.hideVideoIcon;
    },
    displayHeight() {
      return this.small && this.playingVideo === false ? 128 : 256;
    },
    assetReady() {
      const status = this.asset ? this.asset.status : undefined;

      return status === "COMPLETED";
    },
    assetNotReady() {
      const status = this.asset ? this.asset.status : undefined;

      return !!status && status !== "COMPLETED";
    },
    videoReference() {
      return this.asset && this.asset.embeddableVideo
        ? this.asset.embeddableVideo
        : {};
    },
  },
  watch: {
    autoplay() {
      if (this.autoplay && this.hideVideoIcon === false) {
        this.$vuetify.goTo(this.$refs.videoContainer, { offset: 64 });

        this.onPlayVideo();
      }
    },
    isStartingVideo(value) {
      if (value) {
        this.$vuetify.goTo(this.$refs.videoContainer, { offset: 130 });

        if (!this.playingVideo) {
          this.onPlayVideo();
        } else {
          const target = this.$refs.videoPlayer.$el;
          const iframe = target.querySelector("iframe");
          iframe.contentWindow.focus();
        }

        this.$store.dispatch("startVideoForSelectedItem", {
          params: { isStartingVideo: false },
        });
      }
    },
  },
  methods: {
    onDownload() {
      const url = this.fileSource;

      if (url) {
        if (this.isVideo && this.videoUsage) {
          const params = {
            itemId: this.item.id,
            assetId: this.asset.id,
            code: this.videoEvent,
          };
          this.$store.dispatch("registerUsageEvent", { params });
        }

        window.open(url, "_blank");
      }
    },
    onDelete() {
      this.$emit("delete-asset");
    },
    onPlayVideo() {
      const videoId = this.asset.embeddableVideo
        ? this.asset.embeddableVideo.videoId
        : undefined;
      const videoTitle = this.supplementaryTitle || this.item.title;

      if (!videoId) {
        return;
      }

      this.hideVideoPlayer = false;
      this.hideVideoIcon = true;

      this.playingVideo = true;

      this.$nextTick(() => {
        const target = this.$refs.videoPlayer.$el;
        const rect = target.getBoundingClientRect();

        const errorHandler = this.onVimeoError.bind(this);

        this.showVimeoError = false;

        loadVimeoVideoPlayer(
          target,
          videoId,
          videoTitle,
          rect.height,
          rect.width,
          errorHandler
        );

        if (this.videoUsage) {
          const params = {
            itemId: this.item.id,
            assetId: this.asset.id,
            code: this.videoEvent,
          };
          this.$store.dispatch("registerUsageEvent", { params });
        }
      });
    },
    onVimeoError(error) {
      const VIMEO_ERROR = "WAS NOT FOUND";

      const errorString = error ? error.toString().toUpperCase() : "";
      const errorIndex = errorString.indexOf(VIMEO_ERROR);

      if (errorIndex > -1) {
        this.hideVideoPlayer = true;
        this.hideVideoIcon = false;

        this.showVimeoError = true;
      }
    },
    async onRetriggerProcessing() {
      const params = { id: this.asset.id };

      await this.$store.dispatch("retriggerVimeo", { params });

      this.showVimeoError = false;
    },
  },
};
</script>

<style>
.AssetDisplay .v-icon {
  padding-top: 3px;
  padding-left: 12px;
  float: left;
}

.AssetDisplay .hidden {
  display: inline-block;
  opacity: 0;
  margin-top: 3px;
}

.AssetDisplay .trigger:hover > .hidden,
.AssetDisplay .trigger:focus-within > .hidden {
  opacity: 1;
  transition: 0.5s;
}

.AssetDisplay .trigger:hover,
.AssetDisplay .trigger:focus-within {
  opacity: 1;
  min-width: 160px;
  transition: 0.3s;
}

.AssetDisplay button.trigger:hover,
.AssetDisplay button.trigger:focus-within {
  padding-top: 0 !important;
}

.AssetDisplay img {
  height: 100%;
  width: 100%;
  object-fit: contain;
  object-position: 0 100%;
}

.AssetDisplay .untransparant:hover {
  opacity: 1;
  transition: 0.25s;
}

.AssetDisplay .untransparant button {
  margin-left: -6px;
}

.AssetDisplay .v-alert {
  z-index: 9999;
}

.AssetDisplay .full-height {
  height: 100%;
  width: 100%;
}

/* IE10, IE11 */
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .AssetDisplay img.thumbnail {
    height: 100% !important;
    width: auto !important;
    max-height: 100% !important;
    max-width: 100% !important;
  }
}

/* Edge 12-16 */
@supports (-ms-accelerator: true) {
  .AssetDisplay img.thumbnail {
    height: auto !important;
    width: auto !important;
    max-height: 100% !important;
    max-width: 100% !important;
  }
}

/* Edge 16+ */
@supports (-ms-ime-align: auto) {
  .AssetDisplay img.thumbnail {
    height: auto !important;
    width: auto !important;
    max-height: 100% !important;
    max-width: 100% !important;
  }
}

/* Safari 10.1+ */
@media not all and (min-resolution: 0.001dpcm) {
  .AssetDisplay img.thumbnail {
    height: auto !important;
    width: auto !important;
    max-height: 100% !important;
    max-width: 100% !important;
  }
}
</style>
